<template>
  <div
    class="w-full flex-1 flex flex-col items-center justify-between p-3 sm:p-4 bg-gray-950 text-white min-h-[460px] sm:min-h-[520px] rounded-2xl relative overflow-hidden shadow-md">
    <!-- Camera Preview Screen -->
    <div v-if="!capturedImage"
      class="w-full flex-1 flex flex-col items-center justify-center relative min-h-[360px] sm:min-h-[420px]">
      <video ref="videoRef" autoplay playsinline muted
        class="w-full h-full min-h-[360px] sm:min-h-[420px] object-cover rounded-xl bg-black transition-transform duration-300"
        :style="{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }"></video>

      <!-- Watermark overlay on live viewfinder (at bottom, above capture button) -->
      <div v-if="showWatermark"
        class="absolute bottom-28 left-3 right-3 bg-black/75 backdrop-blur-sm p-3 rounded-2xl text-[11px] text-gray-200 pointer-events-none z-10 border border-white/15 space-y-1 shadow-xl">
        <div v-if="agreementNumber" class="font-bold text-white flex items-center gap-1.5">
          <span class="text-blue-400">Agreement No:</span>
          <span>{{ agreementNumber }}</span>
        </div>
        <div class="flex items-center justify-between text-[10px] text-gray-300 font-mono pt-1 border-t border-white/10">
          <span v-if="displayCoordinates">Coord: {{ displayCoordinates }}</span>
          <span>{{ currentFormattedTime }}</span>
        </div>
      </div>

      <!-- Bottom Controls: Capture & Camera Switch Buttons (Side-by-side prominent white buttons matching FinFi App) -->
      <div class="absolute bottom-8 left-0 right-0 flex items-center justify-center gap-10 sm:gap-14 z-30">
        <!-- Capture Shutter Button -->
        <v-btn
          fab
          x-large
          color="white"
          class="shadow-2xl hover:scale-105 active:scale-95 transition-transform"
          :loading="isCapturing"
          :disabled="isCapturing"
          @click="capturePhoto">
          <v-icon color="indigo darken-3" large>mdi-camera</v-icon>
        </v-btn>

        <!-- Camera Switch Button (Large, prominent white circular button matching FinFi App) -->
        <v-btn
          fab
          x-large
          color="white"
          class="shadow-2xl hover:scale-105 active:scale-95 transition-transform"
          :disabled="isCapturing"
          @click="toggleCamera"
          :title="facingMode === 'user' ? 'Switch to Rear Camera' : 'Switch to Front Camera'">
          <v-icon color="indigo darken-3" large>mdi-camera-flip-outline</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Captured Photo Preview & Confirmation -->
    <div v-else class="w-full flex-1 flex flex-col items-center justify-center relative min-h-[360px] sm:min-h-[420px] pb-24 pt-2 px-2">
      <img :src="capturedImage" alt="Captured Selfie"
        class="max-w-full max-h-full object-contain rounded-xl shadow-lg border border-gray-800" />

      <!-- Loading overlay while fetching correct address and stamping -->
      <div v-if="isCapturing" class="absolute inset-0 bg-black/60 rounded-xl flex flex-col items-center justify-center gap-2 z-20 backdrop-blur-xs">
        <v-progress-circular indeterminate color="white" size="36"></v-progress-circular>
        <span class="text-xs text-white font-medium tracking-wide">Stamping location...</span>
      </div>

      <!-- Action Footer -->
      <div v-if="!isCapturing" class="absolute bottom-5 left-0 right-0 flex items-center justify-center gap-4 z-20 px-4">
        <v-btn depressed color="grey darken-3" class="white--text rounded-xl h-10 px-5 text-xs font-bold shadow-md"
          :disabled="uploading || isSubmitting" @click="retakePhoto">
          <v-icon left small>mdi-refresh</v-icon>
          Retake
        </v-btn>

        <v-btn depressed :color="$vuetify.theme.currentTheme.employeePrimary" class="white--text rounded-xl h-10 px-6 text-xs font-extrabold shadow-lg"
          :loading="uploading || isSubmitting" :disabled="uploading || isSubmitting" @click="confirmAndUpload">
          <v-icon left small>mdi-check</v-icon>
          Submit
        </v-btn>
      </div>
    </div>

    <!-- Hidden Canvas used to render capture + stamped watermark -->
    <canvas ref="canvasRef" class="hidden"></canvas>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { getLatLong } from '@/app/utils/helpers'
import { postStepLocationTimeSeries } from '@/app/utils/taskFlow'

export default {
  name: 'AppSelfieCamera',
  props: {
    uploading: {
      type: Boolean,
      default: false
    },
    watermarkText: {
      type: String,
      default: 'FINFI VISIT VERIFICATION'
    },
    taskData: {
      type: Object,
      default: null
    },
    showWatermark: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      stream: null,
      capturedImage: null,
      rawCapturedDataUrl: null,
      capturedAddress: '',
      isCapturing: false,
      isSubmitting: false,
      facingMode: 'user', // 'user' (front) or 'environment' (back)
      currentTime: new Date(),
      currentCoords: null
    }
  },
  computed: {
    agreementNumber() {
      const t = this.taskData || window.task?.value || window.task || null
      return (
        t?.caseNo ||
        t?.case?.caseNo ||
        t?.collectionDetails?.caseNo ||
        t?.additionalInfo?.caseNo ||
        t?.caseNumber ||
        this.$route?.params?.taskId ||
        ''
      )
    },
    displayVisitStartAddress() {
      const t = this.taskData || window.task?.value || window.task || null

      // 1. Check taskStartData (from FinFi App job start)
      const startAddr =
        t?.additionalInfo?.taskStartData?.address ||
        t?.case?.additionalInfo?.taskStartData?.address
      if (startAddr && typeof startAddr === 'string') return startAddr

      // 2. Check localStorage
      const validTaskId =
        t?._id ||
        t?.taskId ||
        t?.case?.tasks?.[0]?._id ||
        this.$route?.params?.taskId
      if (validTaskId) {
        const stored = localStorage.getItem(`taskAddress-${validTaskId}`)
        if (stored) return stored
      }
      if (this.$route?.params?.taskId) {
        const storedRoute = localStorage.getItem(`taskAddress-${this.$route.params.taskId}`)
        if (storedRoute) return storedRoute
      }

      // 3. Check customerAddress (array or string or object)
      const addrList =
        t?.customer?.customerAddress ||
        t?.case?.customer?.customerAddress ||
        t?.customerAddress ||
        t?.collectionDetails?.customerAddress

      if (Array.isArray(addrList) && addrList.length > 0) {
        const a = addrList[0]
        if (typeof a === 'string') return a
        if (typeof a === 'object' && a !== null) {
          const full = [a.addressLine1, a.addressLine2, a.landmark, a.city, a.state, a.pinCode]
            .filter(Boolean)
            .join(', ')
          if (full) return full
        }
      } else if (typeof addrList === 'string' && addrList.trim()) {
        return addrList
      } else if (typeof addrList === 'object' && addrList !== null) {
        const full = [addrList.addressLine1, addrList.addressLine2, addrList.landmark, addrList.city, addrList.state, addrList.pinCode]
          .filter(Boolean)
          .join(', ')
        if (full) return full
      }

      // 4. Check customer object directly
      const cust = t?.customer || t?.case?.customer
      if (cust) {
        if (typeof cust.address === 'string' && cust.address.trim()) return cust.address
        if (typeof cust.customerAddress === 'string' && cust.customerAddress.trim()) return cust.customerAddress
        if (cust.city || cust.state || cust.pinCode) {
          const full = [cust.addressLine1, cust.addressLine2, cust.city, cust.state, cust.pinCode].filter(Boolean).join(', ')
          if (full) return full
        }
      }

      // 5. Check direct address properties
      return t?.address || t?.case?.address || t?.leadDetails?.address || ''
    },
    displayCoordinates() {
      if (this.currentCoords?.latitude && this.currentCoords?.longitude) {
        return `${Number(this.currentCoords.latitude).toFixed(6)}, ${Number(this.currentCoords.longitude).toFixed(6)}`
      }
      const t = this.taskData || window.task?.value || window.task || null
      const startCoords =
        t?.additionalInfo?.taskStartData?.coords ||
        t?.case?.additionalInfo?.taskStartData?.coords
      if (startCoords?.latitude && startCoords?.longitude) {
        return `${Number(startCoords.latitude).toFixed(6)}, ${Number(startCoords.longitude).toFixed(6)}`
      }
      const custCoords = t?.customer?.customerAddress?.[0]?.coordinates || t?.case?.customer?.customerAddress?.[0]?.coordinates
      if (custCoords?.latitude && custCoords?.longitude) {
        return `${Number(custCoords.latitude).toFixed(6)}, ${Number(custCoords.longitude).toFixed(6)}`
      }
      if (t?.latitude && t?.longitude) {
        return `${Number(t.latitude).toFixed(6)}, ${Number(t.longitude).toFixed(6)}`
      }
      return ''
    },
    watermarkTitle() {
      if (this.agreementNumber) {
        return `CASE #${this.agreementNumber}`
      }
      return this.watermarkText
    },
    currentFormattedTime() {
      return dayjs(this.currentTime).format('DD/MM/YYYY hh:mm:ss A')
    }
  },
  mounted() {
    this.initCamera()
    this.fetchCurrentLocation()
    this.timer = setInterval(() => {
      this.currentTime = new Date()
    }, 1000)
  },
  beforeDestroy() {
    this.stopCamera()
    if (this.timer) clearInterval(this.timer)
  },
  methods: {
    async fetchCurrentLocation() {
      try {
        const pos = await getLatLong()
        if (pos?.coords?.latitude && pos?.coords?.longitude) {
          this.currentCoords = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude
          }
        }
      } catch (err) {
        console.warn('Could not fetch GPS coords:', err)
      }
    },
    wrapCanvasText(ctx, text, maxWidth) {
      if (!text) return []
      const words = String(text).split(' ')
      const lines = []
      let currentLine = words[0] || ''

      for (let i = 1; i < words.length; i++) {
        const word = words[i]
        const width = ctx.measureText(currentLine + ' ' + word).width
        if (width < maxWidth) {
          currentLine += ' ' + word
        } else {
          lines.push(currentLine)
          currentLine = word
        }
      }
      if (currentLine) {
        lines.push(currentLine)
      }
      return lines
    },
    async initCamera() {
      try {
        this.stopCamera()
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: this.facingMode,
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false
        })
        if (this.$refs.videoRef) {
          this.$refs.videoRef.srcObject = this.stream
          try {
            await this.$refs.videoRef.play()
          } catch (e) {
            // Autoplay may handle this
          }
        }
      } catch (err) {
        console.error('Camera access error with ideal constraints, retrying with basic facingMode:', err)
        try {
          this.stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: this.facingMode },
            audio: false
          })
          if (this.$refs.videoRef) {
            this.$refs.videoRef.srcObject = this.stream
            await this.$refs.videoRef.play()
          }
        } catch (fallbackErr) {
          console.error('Fallback camera access error:', fallbackErr)
          this.$toast?.error?.('Unable to access device camera. Please grant camera permission.')
        }
      }
    },
    stopCamera() {
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop())
        this.stream = null
      }
    },
    toggleCamera() {
      this.facingMode = this.facingMode === 'user' ? 'environment' : 'user'
      this.initCamera()
    },
    applyWatermark(canvas, { address = '', addressLabel = '', coordinates = '', timestamp = '' } = {}) {
      if (!this.showWatermark) return
      const ctx = canvas.getContext('2d')
      const items = []

      if (this.agreementNumber) {
        items.push({
          label: 'Agreement No',
          value: this.agreementNumber,
          isHeading: true,
          color: '#60a5fa'
        })
      }

      const finalAddress = address || this.displayVisitStartAddress
      if (finalAddress) {
        items.push({
          label: address ? (addressLabel || 'Address') : 'Visit Start Address',
          value: finalAddress,
          color: '#ffffff'
        })
      }

      const coords = coordinates || this.displayCoordinates
      if (coords) {
        items.push({
          label: 'Coordinates',
          value: coords,
          color: '#e2e8f0'
        })
      }

      items.push({
        label: 'Captured on',
        value: timestamp || this.currentFormattedTime,
        color: '#cbd5e1'
      })

      // Sizing & typography proportional to canvas
      const fontSize = Math.max(14, Math.round(canvas.width * 0.028))
      const lineHeight = Math.round(fontSize * 1.35)
      const padding = Math.max(10, Math.round(fontSize * 0.75))
      const margin = 10
      const boxWidth = canvas.width - margin * 2
      const maxTextWidth = boxWidth - padding * 2 - 14

      ctx.font = `600 ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`

      // Break lines with text wrapping
      const printableLines = []
      items.forEach((item) => {
        const fullText = `${item.label}: ${item.value}`
        const wrapped = this.wrapCanvasText(ctx, fullText, maxTextWidth)
        wrapped.forEach((lineText, idx) => {
          printableLines.push({
            text: lineText,
            color: item.color,
            isHeading: item.isHeading && idx === 0
          })
        })
      })

      const boxHeight = printableLines.length * lineHeight + padding * 2
      const boxY = canvas.height - boxHeight - margin
      const boxX = margin

      // Draw backdrop (rounded dark container)
      ctx.fillStyle = 'rgba(15, 23, 42, 0.82)'
      if (ctx.roundRect) {
        ctx.beginPath()
        ctx.roundRect(boxX, boxY, boxWidth, boxHeight, 10)
        ctx.fill()
      } else {
        ctx.fillRect(boxX, boxY, boxWidth, boxHeight)
      }

      // Draw subtle border
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.16)'
      ctx.lineWidth = 1
      ctx.stroke()

      // Draw left blue indicator bar
      ctx.fillStyle = '#3b82f6'
      if (ctx.roundRect) {
        ctx.beginPath()
        ctx.roundRect(boxX + 3, boxY + 4, 4, boxHeight - 8, 2)
        ctx.fill()
      } else {
        ctx.fillRect(boxX + 3, boxY + 4, 4, boxHeight - 8)
      }

      // Draw text lines
      printableLines.forEach((lineObj, idx) => {
        ctx.fillStyle = lineObj.color
        ctx.font = lineObj.isHeading
          ? `bold ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
          : `500 ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
        const y = boxY + padding + lineHeight * (idx + 0.8)
        ctx.fillText(lineObj.text, boxX + padding + 8, y)
      })
    },
    async capturePhoto() {
      const video = this.$refs.videoRef
      const canvas = this.$refs.canvasRef
      if (!video || !canvas || this.isCapturing) return

      canvas.width = video.videoWidth || 640
      canvas.height = video.videoHeight || 480
      const ctx = canvas.getContext('2d')

      // Draw mirrored video if user camera
      if (this.facingMode === 'user') {
        ctx.translate(canvas.width, 0)
        ctx.scale(-1, 1)
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        ctx.setTransform(1, 0, 0, 1, 0, 0)
      } else {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
      }

      // Immediately freeze video frame onto canvas & show preview
      this.rawCapturedDataUrl = canvas.toDataURL('image/jpeg', 0.95)
      this.capturedImage = this.rawCapturedDataUrl
      this.isCapturing = true

      let actualAddress = ''
      let actualCoordinates = ''

      // Fetch correct reverse-geocoded address from location time series API
      try {
        const timeSeriesRes = await postStepLocationTimeSeries('AppTaskSelfie', this.taskData, {
          coords: this.currentCoords
        })
        if (timeSeriesRes?.success && timeSeriesRes?.address) {
          actualAddress = timeSeriesRes.address
        }
        const coordsArr = timeSeriesRes?.coordinates || timeSeriesRes?.data?.location?.coordinates
        if (Array.isArray(coordsArr) && coordsArr.length >= 2) {
          // GeoJSON Point: [longitude, latitude]
          const lng = Number(coordsArr[0]).toFixed(6)
          const lat = Number(coordsArr[1]).toFixed(6)
          actualCoordinates = `${lat}, ${lng}`
        }
      } catch (err) {
        console.warn('Could not retrieve actual address from location time series on capture:', err)
      }

      const finalAddress = actualAddress || this.displayVisitStartAddress
      const finalCoords = actualCoordinates || this.displayCoordinates
      this.capturedAddress = finalAddress

      // Cache address in localStorage for subsequent steps
      if (actualAddress) {
        const validTaskId =
          this.taskData?._id ||
          this.taskData?.taskId ||
          this.taskData?.case?.tasks?.[0]?._id ||
          this.$route?.params?.taskId
        if (validTaskId) {
          localStorage.setItem(`taskAddress-${validTaskId}`, actualAddress)
        }
      }

      // Stamp watermark with correct location on captured photo
      this.applyWatermark(canvas, {
        address: finalAddress,
        addressLabel: 'Address',
        coordinates: finalCoords,
        timestamp: this.currentFormattedTime
      })

      this.capturedImage = canvas.toDataURL('image/jpeg', 0.85)
      this.isCapturing = false
    },
    retakePhoto() {
      this.capturedImage = null
      this.rawCapturedDataUrl = null
      this.capturedAddress = ''
      this.isCapturing = false
      this.$nextTick(() => {
        if (this.$refs.videoRef && this.stream) {
          this.$refs.videoRef.srcObject = this.stream
          this.$refs.videoRef.play?.().catch(() => {})
        } else {
          this.initCamera()
        }
      })
    },
    confirmAndUpload() {
      if (!this.capturedImage || this.isSubmitting || this.uploading || this.isCapturing) return

      try {
        this.isSubmitting = true

        // Convert base64 DataURL to Blob File
        const byteString = atob(this.capturedImage.split(',')[1])
        const mimeString = this.capturedImage.split(',')[0].split(':')[1].split(';')[0]
        const ab = new ArrayBuffer(byteString.length)
        const ia = new Uint8Array(ab)
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i)
        }
        const blob = new Blob([ab], { type: mimeString })
        const file = new File([blob], `selfie_${Date.now()}.jpg`, { type: mimeString })

        this.$emit('captured', {
          file,
          dataUrl: this.capturedImage,
          address: this.capturedAddress || this.displayVisitStartAddress
        })
      } catch (error) {
        console.error('Error in confirmAndUpload:', error)
        this.$toast?.error?.('Error processing image. Please try again.')
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

